import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'mgdlead.theme';

/**
 * Script para colar INLINE no <head> do index.html, antes do CSS/app.
 * Sem ele a página pinta claro e "pisca" para escuro quando o React monta.
 *
 *   <script>{THEME_INIT_SCRIPT}</script>
 *
 * Ele só escreve `data-theme` quando a preferência é EXPLÍCITA. No modo
 * "system" o atributo fica ausente e quem decide é o `@media
 * (prefers-color-scheme)` do theme.css — nenhum JS no caminho.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem("${THEME_STORAGE_KEY}");if(t==="dark"||t==="light"){document.documentElement.setAttribute("data-theme",t)}else{document.documentElement.removeAttribute("data-theme")}}catch(e){}})();`;

function readStored(): ThemePreference {
  // localStorage lança em navegador privado / cookies bloqueados
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* sem persistência: cai para "system", que sempre funciona */
  }
  return 'system';
}

function writeStored(theme: ThemePreference): void {
  try {
    if (theme === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* preferência vale só nesta aba; a UI continua correta */
  }
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToDocument(theme: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export interface UseThemeResult {
  /** O que o usuário escolheu. */
  theme: ThemePreference;
  /** O que está na tela agora (resolve "system"). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
  /** light -> dark -> system -> light. */
  cycleTheme: () => void;
}

/**
 * Tema light/dark/system.
 * O valor inicial vem do que o THEME_INIT_SCRIPT já escreveu no <html>,
 * então a primeira renderização já nasce no tema certo (sem flash).
 */
export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<ThemePreference>(() =>
    typeof window === 'undefined' ? 'system' : readStored(),
  );
  const [systemResolved, setSystemResolved] = useState<ResolvedTheme>(() => systemTheme());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setSystemResolved(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // outra aba trocou o tema
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      const next = readStored();
      setThemeState(next);
      applyToDocument(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    applyToDocument(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    writeStored(next);
    applyToDocument(next);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: ThemePreference =
        current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      writeStored(next);
      applyToDocument(next);
      return next;
    });
  }, []);

  return {
    theme,
    resolvedTheme: theme === 'system' ? systemResolved : theme,
    setTheme,
    cycleTheme,
  };
}
